import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ErrorMsg, Loading, Section } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import type { Brand, BrandLogo, BrandSpec, StyleName, TemplateRow } from '../../lib/contentTypes';
import { FORMAT_LABEL, uploadAssets, useContentMeta } from './common';
import { useVideoMeta } from './Videos';
import { VoicePanel } from './Voice';

const STYLES: StyleName[] = ['title', 'subtitle', 'body', 'caption', 'label'];

export function BrandPanel() {
  const meta = useContentMeta();
  const [brand, setBrand] = useState<Brand | null>(null);
  const [spec, setSpec] = useState<BrandSpec | null>(null);
  const [name, setName] = useState('');
  const [logoId, setLogoId] = useState<string | null>(null); // the default logo's asset id
  const [logos, setLogos] = useState<BrandLogo[]>([]); // every saved or just-uploaded version
  const [progress, setProgress] = useState('');
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(() => {
    api<Brand>('/api/content/brand').then((b) => {
      setBrand(b);
      setSpec(b.spec);
      setName(b.name);
      setLogoId(b.logo_asset_id);
      setLogos(b.logos);
    });
    api<{ items: TemplateRow[] }>('/api/content/templates').then((d) => setTemplates(d.items));
  }, []);
  useEffect(load, [load]);
  if (!brand || !spec) return <Loading what="Loading the brand kit" />;

  const setStyle = (s: StyleName, k: string, v: string | number | boolean) => setSpec({ ...spec, styles: { ...spec.styles, [s]: { ...spec.styles[s], [k]: v } } });
  async function save() {
    setError('');
    setMsg('');
    try {
      const b = await api<Brand>('/api/content/brand', { method: 'PUT', body: { name, spec, logo_asset_id: logoId, logos: logos.map((l) => ({ asset_id: l.asset_id, name: l.name })), version: brand!.version },
      });
      setBrand(b);
      setLogos(b.logos);
      setLogoId(b.logo_asset_id);
      setMsg('Brand kit saved. Every item uses it at its next render; approved items keep their approval until edited.');
    } catch (e) {
      setError(errorText(e));
    }
  }
  async function uploadLogos(files: FileList | null) {
    if (!files?.length) return;
    setError('');
    const { done, errors } = await uploadAssets(Array.from(files), { kind: 'logo', onProgress: setProgress });
    if (errors.length) setError(errors.join('; '));
    // Each file becomes a version named after the file; the same picture twice is one version.
    const next = [...logos];
    for (const a of done) {
      if (next.some((l) => l.asset_id === a.id)) continue;
      const base = a.filename.replace(/\.[^.]+$/, '').trim().slice(0, 80) || 'Logo';
      let name = base;
      for (let n = 2; next.some((l) => l.name.toLowerCase() === name.toLowerCase()); n++) name = `${base.slice(0, 74)} ${n}`;
      next.push({ asset_id: a.id, name, asset: a });
    }
    setLogos(next);
    if (!logoId && next.length) setLogoId(next[0].asset_id);
    setMsg(done.length ? 'Logos added below. Save the brand kit to keep them.' : '');
  }
  function removeLogo(id: string) {
    const rest = logos.filter((l) => l.asset_id !== id);
    setLogos(rest);
    if (logoId === id) setLogoId(rest[0]?.asset_id ?? null);
  }
  async function deleteTemplate(t: TemplateRow) {
    try {
      await api(`/api/content/templates/${t.id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setError(errorText(e));
    }
  }
  return (
    <>
      <Section title="Brand kit" aside={<span className="muted small">Placeholder values until the real brand files arrive (open question 5)</span>}>
        <div className="stack">
          <label className="field cs-narrow">
            <span>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <fieldset className="field">
            <legend>Colours</legend>
            <div className="cs-colors">
              {spec.colors.map((c, i) => (
                <div key={i} className="cs-color">
                  <input type="color" value={c.hex} aria-label={`${c.name} colour`} onChange={(e) => setSpec({ ...spec, colors: spec.colors.map((x, j) => (j === i ? { ...x, hex: e.target.value } : x)) })} />
                  <input value={c.name} aria-label="Colour name" onChange={(e) => setSpec({ ...spec, colors: spec.colors.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                  <span className="mono small">{c.hex}</span>
                  <button type="button" className="btn ghost small" onClick={() => setSpec({ ...spec, colors: spec.colors.filter((_, j) => j !== i) })} disabled={spec.colors.length <= 1}>
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="btn small" onClick={() => setSpec({ ...spec, colors: [...spec.colors, { name: 'New', hex: '#777777' }] })}>
                Add colour
              </button>
            </div>
          </fieldset>
          <fieldset className="field">
            <legend>Typography (size in % of the artboard width, so one style fits every export size)</legend>
            <div className="table-wrap">
              <table className="table compact">
                <thead>
                  <tr>
                    <th>Style</th>
                    <th>Font</th>
                    <th>Weight</th>
                    <th>Size %</th>
                    <th>Line height</th>
                    <th>Capitals</th>
                  </tr>
                </thead>
                <tbody>
                  {STYLES.map((s) => (
                    <tr key={s}>
                      <td>{s}</td>
                      <td>
                        <input list="cs-fonts" value={spec.styles[s].font} aria-label={`${s} font`} onChange={(e) => setStyle(s, 'font', e.target.value)} />
                      </td>
                      <td>
                        <input type="number" min={100} max={900} step={100} value={spec.styles[s].weight} aria-label={`${s} weight`} onChange={(e) => setStyle(s, 'weight', Number(e.target.value))} />
                      </td>
                      <td>
                        <input type="number" min={0.5} max={40} step={0.1} value={spec.styles[s].size_pct} aria-label={`${s} size`} onChange={(e) => setStyle(s, 'size_pct', Number(e.target.value))} />
                      </td>
                      <td>
                        <input type="number" min={0.8} max={3} step={0.05} value={spec.styles[s].line_height} aria-label={`${s} line height`} onChange={(e) => setStyle(s, 'line_height', Number(e.target.value))} />
                      </td>
                      <td>
                        <input type="checkbox" checked={spec.styles[s].uppercase} aria-label={`${s} capitals`} onChange={(e) => setStyle(s, 'uppercase', e.target.checked)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <datalist id="cs-fonts">
              {meta?.fonts.map((f) => (
                <option key={f.family} value={f.family} />
              ))}
            </datalist>
            <p className="muted small">
              Bundled fonts: {meta?.fonts.map((f) => `${f.family} (${f.licence})`).join(', ')}. Any other name shows a “missing font” warning and blocks approval until the font is bundled.
            </p>
          </fieldset>
          <div className="row gap wrap">
            <label className="field">
              <span>Safe margin % of width</span>
              <input type="number" min={0} max={25} step={0.5} value={spec.safe_margin_pct} onChange={(e) => setSpec({ ...spec, safe_margin_pct: Number(e.target.value) })} />
            </label>
          </div>
          <fieldset className="field" aria-label="Logo versions">
            <legend>Logo versions (up to 12). The default one is used wherever a logo box has no version chosen.</legend>
            {logos.length === 0 && <p className="muted small">No logo yet.</p>}
            <ul className="plain cs-logo-list">
              {logos.map((l) => (
                <li key={l.asset_id} className="row gap center wrap" data-logo={l.asset_id}>
                  <img src={l.asset.urls.thumb} alt={l.asset.filename} className="cs-logo-thumb" />
                  <input value={l.name} maxLength={80} aria-label="Logo version name" onChange={(e) => setLogos(logos.map((x) => (x.asset_id === l.asset_id ? { ...x, name: e.target.value } : x)))} />
                  <label className="check small">
                    <input type="radio" name="default-logo" checked={logoId === l.asset_id} onChange={() => setLogoId(l.asset_id)} aria-label={`Use ${l.name} as the default logo`} /> default
                  </label>
                  <span className="muted small num">
                    {l.asset.width}×{l.asset.height}
                  </span>
                  <button type="button" className="btn ghost small" onClick={() => removeLogo(l.asset_id)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <div className="row gap center wrap">
              <label className="btn file-btn small">
                Upload logos
                <input type="file" accept="image/png,image/jpeg" multiple onChange={(e) => { void uploadLogos(e.target.files); e.target.value = ''; }} aria-label="Upload logos" />
              </label>
              <span className="muted small">PNG with transparency recommended; export SVG or PDF logos as PNG first. Pick several files at once.</span>
              {progress && <span className="small" role="status">{progress}</span>}
            </div>
          </fieldset>
          <div className="row gap center">
            <button type="button" className="btn primary" onClick={() => void save()}>
              Save brand kit
            </button>
            <span className="muted small">Owner only.</span>
          </div>
          {msg && <p className="msg ok">{msg}</p>}
          <ErrorMsg>{error}</ErrorMsg>
          <MirageVideoBrandSync />
        </div>
      </Section>
      <Section title="Templates">
        <p className="muted small">
          Built-in templates are kept as shipped. Make, edit and publish your own in the <Link to="/content/templates">template maker</Link>, or edit an item and use “Save as template” in its editor.
        </p>
        <ul className="plain cs-template-list">
          {templates.map((t) => (
            <li key={t.id} className="row gap center wrap">
              <strong>{t.name}</strong>
              <span className="muted small">
                {FORMAT_LABEL[t.format]} · {t.layout.artboards.length} artboard{t.layout.artboards.length === 1 ? '' : 's'} · {t.is_builtin ? 'built-in' : t.status === 'draft' ? 'yours, Draft (not offered yet)' : `yours, revision ${t.revision}${t.unpublished_changes ? ', unpublished changes' : ''}`}
              </span>
              {!t.is_builtin && (
                <button type="button" className="btn ghost small" onClick={() => void deleteTemplate(t)}>
                  Delete
                </button>
              )}
              {t.description && <span className="small grow">{t.description}</span>}
            </li>
          ))}
        </ul>
      </Section>
      <VoicePanel />
    </>
  );
}

/** Brand kit → Mirage Video: the logo versions into its library; fonts, colours and tone go with each
 *  project made from here (decision 86). Shown to owners once Mirage Video is linked. */
function MirageVideoBrandSync() {
  const meta = useVideoMeta();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ logos: { name: string; existing?: boolean; error?: string }[]; style_notes: string[] } | null>(null);
  if (!meta?.configured || !meta.can_make) return null;
  async function send() {
    setBusy(true);
    setError('');
    try {
      setResult(await api('/api/content/brand/miragevideo', { method: 'POST' }));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <fieldset className="field cs-mv-brand">
      <legend>Mirage Video</legend>
      <p className="muted small">
        Sends every saved logo version to Mirage Video's logo library (each is stored there once). The fonts, colours and tone of voice go with every video made from here. Save the brand kit first.
      </p>
      <div className="row gap center wrap">
        <button type="button" className="btn small" disabled={busy} onClick={() => void send()}>
          {busy ? 'Sending…' : 'Send to Mirage Video'}
        </button>
      </div>
      {result && (
        <div className="small" role="status">
          <p>
            {result.logos.map((l) => `${l.name}: ${l.error ? l.error : l.existing ? 'already there' : 'sent'}`).join(' · ')}
          </p>
          {result.style_notes.length > 0 && <p className="warn-text">{result.style_notes.join(' · ')}</p>}
        </div>
      )}
      <ErrorMsg>{error}</ErrorMsg>
    </fieldset>
  );
}
